import { withoutUnofferedSmsFields } from "@/lib/sms/smsCapabilities";
import { DataType } from "@/types/enums/DataType";
import { Field, isComputedField } from "@/types/Field";

/**
 * Single source of truth for which fields the CSV/Excel import mapper may
 * target. Previously duplicated as "twin lists" in useMapper (auto-match +
 * setField validation) and InputColumn (the mapper's field dropdown) that had
 * to stay byte-identical by hand — one predicate now serves all three so they
 * can't drift.
 */

export type ImportScope = "guest" | "contact";

/**
 * Data types that are never import targets in any scope:
 *  - UNKNOWN → no typed parser/validator for the column values.
 *  - IMAGE   → values are set exclusively via the per-guest upload flow,
 *              never importable from a CSV/Excel column.
 */
export const IMPORT_EXCLUDED_DATA_TYPES: ReadonlySet<DataType> = new Set([
  DataType.UNKNOWN,
  DataType.IMAGE,
]);

/**
 * Built-in guest fields the import job may write. An ALLOW-list on purpose,
 * with two independent admission rules:
 *
 *  1. The backend import actually WRITES it (a narrowing of the Guest.builder()
 *     copy list in importGuestsCreate/-Overwrite — main-backend
 *     HeavyProcessingService — plus `guestId`, the overwrite-mode mapping key).
 *     The backend silently drops every other key, so offering a field outside
 *     this set (state rollups, lastEmail*, origin/importId provenance,
 *     seat/appointment aggregates, …) lets the user map a column that then
 *     imports nothing.
 *  2. The field is safe to write BLIND — its validity must not depend on the
 *     value of another field. An import row is a PARTIAL document: it is merged
 *     over whatever is stored, so a field whose correctness is a joint property
 *     of two fields can be made self-contradictory by a row that mentions only
 *     one of them.
 *
 * Rule 2 is why `guestType` is NOT here and must not be re-added. It is
 * meaningless without `mainGuest`: a row carrying only `guestType:"mainGuest"`
 * collides with a stored `mainGuest` it never mentions, producing
 * `{guestType:"mainGuest", mainGuest:<old parent>}` — a shape no API can
 * repair, since `mainGuest` is not clearable on the update path for that row.
 *
 * Two claims that used to stand here are no longer true and are corrected
 * rather than deleted, because both were load-bearing arguments:
 *   - "the update endpoint refuses every guestType change" — it does not.
 *     `GuestService.validateGuestTypeUpdate` allows both directions today,
 *     refusing only while a request is PENDING, and clears the stranded parent
 *     in the same write.
 *   - "`mainGuest` is likewise absent" — it is an import target now (the party
 *     link). What keeps `guestType` out is rule 2 alone: it is DERIVED from
 *     whether the party column carries a value, so mapping it would be a
 *     second, contradictable source for the same fact.
 *
 * Everything not listed gets the "system export column – will be skipped" hint
 * instead. Custom additional fields are always importable (data type only).
 */
export const GUEST_IMPORT_TARGET_FIELD_NAMES: ReadonlySet<string> = new Set([
  "guestId",
  // The party link. The cell holds any key that identifies the main guest — a
  // 24-hex id (which is what an export writes), a booking code, or an email —
  // and the producer resolves it against this file first and the event second
  // (ImportPartyResolver). Empty cell = the row is a main guest itself.
  //
  // It rides to the backend in the job's `partyKeys` map rather than on the
  // row, because Guest.mainGuest is an ObjectId there and an email in it fails
  // at binding time. See PImport/utils/partyKeys.ts.
  //
  // `guestType` stays OUT, and admission rule 2 above is why: it is meaningless
  // without a parent, and a row carrying only a type collides with a stored
  // parent it never mentions. Nothing is lost by that — the type is DERIVED
  // from whether this column has a value, which is the same rule the backend
  // already applies when creating a guest through the API.
  "mainGuest",
  // The party ALIAS: a label the operator gives a main-guest row ("Familie
  // Müller"), which the companions then write into `mainGuest`. Emails are
  // rarely unique in a customer's event (a family shares one address) and
  // new guests have no id yet — this is the key that is always unique
  // because the operator makes it so. File-scoped: never stored, never
  // queried; it rides in the job's `partyAliases` map exactly like
  // `partyKeys` (PImport/utils/partyKeys.ts) and the producer offers it from
  // main-guest rows only (ImportPartyResolver).
  "partyAlias",
  "state",
  "gender",
  "emailOptIn",
  // SMS channel wave: the consumer import writes it (and a `guest_consents`
  // row, source IMPORT) while the backend switch is on and strips it while
  // off; the mapper only ever sees the field while the channel is on, because
  // FieldsProvider drops it otherwise.
  "smsOptIn",
  "title",
  "firstName",
  "lastName",
  "company",
  "jobTitle",
  "address",
  "address2",
  "zipCode",
  "city",
  "country",
  "email",
  "phone",
  "mobile",
  "language",
]);

/**
 * The party-link column, offered by the import mapper only.
 *
 * Deliberately NOT added to FIELDS_GUEST. That list drives the guest table's
 * columns, the filter builder and the display config, and none of them wants a
 * raw 24-hex parent id as a pickable column — the guest list already expresses
 * the relation through `companions` and `numCompanions`. The import is the one
 * surface where a person types the link by hand, so the field exists only
 * here.
 *
 * STRING, not the SINGLE_SELECT the guest-table picker would use: the cell is
 * free text (an id, a booking code, an email), and an option-less select
 * reports every non-empty cell as invalid (collectImportErrors →
 * isValidForType).
 *
 * `editable: true` because the mapper's own validation reads it; nothing
 * writes this field on a guest — it leaves as `partyKeys`.
 */
export const MAIN_GUEST_IMPORT_FIELD: Field = {
  name: "mainGuest",
  dataType: DataType.STRING,
  additionalField: false,
  editable: true,
};

/**
 * The party-alias column, the import mapper's second synthetic field. Same
 * reasoning as MAIN_GUEST_IMPORT_FIELD: not a guest field anywhere else, free
 * text, leaves the browser in `partyAliases` and never on the row.
 */
export const PARTY_ALIAS_IMPORT_FIELD: Field = {
  name: "partyAlias",
  dataType: DataType.STRING,
  additionalField: false,
  editable: true,
};

/**
 * The two party columns, in the order they are offered. `partyAlias` is LAST
 * on purpose: the auto-matcher keeps the first exact hit, so a custom
 * additional field an admin labelled "Key" keeps winning its own header
 * (useMapper.partyAlias test pins that).
 */
export const PARTY_IMPORT_FIELDS: readonly Field[] = [
  MAIN_GUEST_IMPORT_FIELD,
  PARTY_ALIAS_IMPORT_FIELD,
];

/**
 * The arrival-notice column (W4 Hop B, N29): one cell names up to
 * ARRIVAL_NOTICE_MAX_PER_CELL addresses that get an email when THIS row's
 * guest arrives. Same reasoning as the party columns — free text, import
 * only, in no FIELDS_* catalogue — and it never travels on the row: the
 * addresses are turned into contact ids by `ensure` and leave as the job's
 * `arrivalNoticeLinks` (ids only, contract §7).
 *
 * Offered ONLY through `includeArrivalNotice`, which the import mapper sets
 * for an OWNER / ADMIN on the event guest import (the backend's door admits
 * nobody else). Never the dashboard widget editors, never the AI import tools
 * (they build their own field list and never see this one).
 */
export const ARRIVAL_NOTICE_IMPORT_FIELD: Field = {
  name: "arrivalNotice",
  dataType: DataType.STRING,
  additionalField: false,
  editable: true,
};

/**
 * The full set of fields the guest/contact import mapper may target: the
 * importable subset of `allFields`, and — only when asked — the synthetic
 * party-link column and the arrival-notice column.
 *
 * One function for the auto-matcher (useMapper), the dropdown (InputColumn)
 * and setField's validation, so a field offered in one cannot be refused by
 * another — the drift `isImportableField` was introduced to end.
 *
 * `includePartyLink` defaults to FALSE, and that default is load-bearing.
 * `InputColumn`'s `type === "guest" && !filtering` branch reads like an
 * import-only lane and is not: `FieldsInput` hardcodes `filtering={false}` and
 * four dashboard widget editors mount it with `type="guest"`. Appending the
 * synthetic column unconditionally offered `mainGuest` there — past
 * `filterDashboardSelectableFields`, which curates that list for PII — and the
 * backend's fail-closed `DashboardDataValidation.ALLOWED_PRE_SELECTORS` has no
 * `mainGuest`, so picking it 400s the card. Opt-in, never opt-out.
 *
 * `smsOffered` (SMS plugin wave, Q3) — the guest-import path passes it
 * explicitly: false drops the SMS consent field from the mapper's targets and
 * its auto-match while the event's SMS plugin is off. Default true, so every
 * other caller (the dashboard widget pickers) is unchanged.
 */
export const importFieldUniverse = (
  allFields: Field[],
  scope: ImportScope,
  {
    includePartyLink = false,
    includeArrivalNotice = false,
    smsOffered = true,
  }: { includePartyLink?: boolean; includeArrivalNotice?: boolean; smsOffered?: boolean } = {},
): Field[] => {
  const base = withoutUnofferedSmsFields(
    allFields.filter((field) => isImportableField(field, scope)),
    smsOffered,
  );
  if (scope !== "guest") return base;
  // `includeArrivalNotice` defaults to FALSE for the same load-bearing reason
  // as `includePartyLink` (see above) — and one more: only an OWNER / ADMIN
  // may link arrival-notice recipients, so the caller decides per role.
  const withNotice = (fields: Field[]): Field[] =>
    includeArrivalNotice &&
    !fields.some((field) => field.name === ARRIVAL_NOTICE_IMPORT_FIELD.name && !field.additionalField)
      ? [...fields, ARRIVAL_NOTICE_IMPORT_FIELD]
      : fields;
  if (!includePartyLink) return withNotice(base);
  // Guard against FIELDS_GUEST later growing its own mainGuest/partyAlias
  // entry: prefer the real one, coerced to the shape the import needs, over a
  // duplicate. `!additionalField` matters — a CUSTOM field an admin happened
  // to name `mainGuest` or `partyAlias` is a different thing entirely (it
  // lands in additionalFields, parseGuest routes it there before the built-in
  // switch), and treating it as the party column would make the operator's
  // own column disappear for that event.
  let out = base;
  for (const synthetic of PARTY_IMPORT_FIELDS) {
    const existing = out.find(
      (field) => field.name === synthetic.name && !field.additionalField,
    );
    out = existing
      ? out.map((field) =>
          field.name === synthetic.name && !field.additionalField
            ? { ...field, dataType: DataType.STRING, options: undefined }
            : field,
        )
      : [...out, synthetic];
  }
  return withNotice(out);
};

/** Contact pool: every core contact field is a plain writable data field —
 *  only the server-minted timestamps are off limits. */
export const CONTACT_IMPORT_EXCLUDED_FIELD_NAMES: ReadonlySet<string> =
  new Set(["createdAt", "updatedAt"]);

/** True when the import mapper may offer/auto-match this field in `scope`. */
export const isImportableField = (
  field: Field,
  scope: ImportScope,
): boolean => {
  if (IMPORT_EXCLUDED_DATA_TYPES.has(field.dataType)) return false;
  // A COMPUTED field is derived from other columns and re-derived after every
  // write: an import that mapped a spreadsheet column onto it would report
  // success and land nothing, because the derive overwrites the imported value
  // in the same job. The backend refuses the write too — this keeps the column
  // out of the picker so nobody maps it in the first place.
  if (isComputedField(field)) return false;
  if (field.additionalField) return true;
  return scope === "contact"
    ? !CONTACT_IMPORT_EXCLUDED_FIELD_NAMES.has(field.name)
    : GUEST_IMPORT_TARGET_FIELD_NAMES.has(field.name);
};
